export const getSNItemsFormatted = (list) => {
  const itemsFormatted = [];
  list.forEach((ticket) => {
    let currReq;
    if (ticket.items.length > 1) {
      currReq = _handleReqsWithMultipleItems(
        ticket.items,
        ticket.requested_for
      );
      itemsFormatted.push(...currReq);
    } else {
      currReq = _createSNTicketObj(ticket.items[0], ticket.requested_for);
      itemsFormatted.push(currReq);
    }
  });
  return itemsFormatted;
};

export const handleTicketNameFormat = (orderGuideName, ticketName) => {
  return orderGuideName !== undefined
    ? `${orderGuideName} - ${ticketName}`
    : ticketName;
};

const _createSNTicketObj = (ticket, requestedFor) => {
  let item = {
    ticketId: ticket.request_item_id,
    name: ticket.cat_item_name,
    status: ticket.status,
    lastUpdate: ticket.last_updated,
    openedAt: ticket.opened_at,
    submittedDate: ticket.opened_at,
    closedAt: ticket.closed_at,
    type: ticket.type,
    priority: ticket.priority,
    person: requestedFor,
    source: 'sn',
    cat_sys_id: ticket.cat_item_sys_id
  };
  if (ticket.isFromOrderGuide) {
    item.orderGuideName = ticket.orderGuideName;
  }
  return item;
};

const _handleReqsWithMultipleItems = (ritms, requestedFor) => {
  return ritms.map((item) => _createSNTicketObj(item, requestedFor));
};


/**
 * Formats a ServiceNow ticket object for the Ticket Journey UI.
 * - Processes main ticket details.
 * - Filters the 'journal' array to only include customer-visible 'comments'.
 * - Formats the 'attachments' array with necessary details, including a download URL.
 */
export const setSMTItemForTicketJourneyFromServiceNow = (ticket) => {
  // --- Helper to map ServiceNow states to UI-friendly statuses ---
  const statusUIMapping = {
    'On Hold': 'Pending',
    'Resolved': 'Resolved',
    'Closed': 'Closed',
    'In Progress': 'In Progress',
    'New': 'Created',
  };

  // --- Helper to format date strings (YYYY-MM-DD HH:MM:SS -> YYYY/MM/DD) ---
  const formatDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return '';
    const datePart = dateString.split(' ')[0];
    return datePart.replace(/-/g, '/');
  };

  // --- Helper to determine the ticket type label ---
  const getTypeLabel = (category) => {
    return category?.toLowerCase() === "incident" ? 'failure' : 'request';
  };

  /**
   * Processes the 'journal' array.
   * Maps journal entries to a clean { author, createdDate, text } shape.
   * Note: 'system' entries are filtered out by the BE before reaching the FE.
   */
  const formatComments = (journalEntries = []) => {
    return journalEntries
      .map(entry => ({
        author: entry.sys_created_by,
        createdDate: entry.sys_created_on,
        text: entry.value,
      }));
  };


  /**
   * Processes the 'attachments' array.
   * It creates a clean list of files with a direct download URL.
   */
  const formatAttachments = (attachments = []) => {
    return attachments.map(attachment => ({
      id: attachment.sys_id,
      fileName: attachment.file_name,
      author: attachment.sys_created_by,
      createdDate: attachment.sys_created_on,
      contentType: attachment.content_type,
      sizeBytes: attachment.size_bytes,
      downloadUrl: attachment.sys_id,
    }));
  };

  // --- Main Formatting Logic ---
  const stateText = ticket['state$DISPLAY'] || 'Unknown';
  const status = statusUIMapping[stateText] || stateText;

  const baseFormattedItem = {
    // Core Ticket Details
    activity_id: ticket.number,
    serviceName: ticket.short_description,
    status: status,
    lastDateTime: formatDate(ticket.sys_updated_on),
    originalLastDateTime: ticket.sys_updated_on,
    serviceDescription: ticket.description,
    submittedDate: formatDate(ticket.opened_at),
    originalSubmittedDate: ticket.opened_at,
    sys_id: ticket.sys_id,
    priority: ticket['priority$DISPLAY'],
    type: getTypeLabel(ticket.category),

    // People
    performer: ticket.opened_by?.user_name || '',
    requested_for: ticket.caller_id?.user_name || '',

    // Empty fields for consistency
    taskDate: '',
    cost: '',
    onBehalf: '',
    requisitionDueDate: '',
    rejection_reason: '',
    cmdb_ci: ticket.cmdb_ci?.name || '',

    // Processed Comments and Attachments
    comments: formatComments(ticket.journal),
    attachments: formatAttachments(ticket.attachments),
  };

  // Add state-specific fields (e.g., resolution notes for Closed tickets)
  let itemsFormatted = { ...baseFormattedItem };
  switch (status) {
    case 'Resolved':
      itemsFormatted.resolution = ticket.close_notes;
      itemsFormatted.closedDate = formatDate(ticket.resolved_at);
      break;
    case 'Pending':
      itemsFormatted.statusReason = ticket['hold_reason$DISPLAY'];
      break;
    case 'Closed':
      itemsFormatted.resolution = ticket.close_notes;
      itemsFormatted.closedDate = formatDate(ticket.closed_at);
      break;
    default:
      break;
  }

  return itemsFormatted;
};

export const setUniversalRequestForTicketJourney = (ticket) => {
  const statusUIMapping = {
    'On Hold': 'Pending',
    'Awaiting Response': 'Awaiting Response',
    'Resolved': 'Resolved',
    'Closed': 'Closed',
    'In Progress': 'In Progress',
    'New': 'Created',
  };

  const formatDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return '';
    const datePart = dateString.split(' ')[0];
    return datePart.replace(/-/g, '/');
  };

  const formatComments = (journalEntries = []) => {
    return journalEntries.map(entry => ({
      author: entry.sys_created_by,
      createdDate: entry.sys_created_on,
      text: entry.value,
    }));
  };

  const formatAttachments = (attachments = []) => {
    return attachments.map(attachment => ({
      id: attachment.sys_id,
      fileName: attachment.file_name,
      author: attachment.sys_created_by,
      createdDate: attachment.sys_created_on,
      contentType: attachment.content_type,
      sizeBytes: attachment.size_bytes,
      downloadUrl: attachment.sys_id,
    }));
  };

  const stateText = ticket['state$DISPLAY'] || ticket.state || 'Unknown';
  const status = statusUIMapping[stateText] || stateText;

  const formattedItem = {
    activity_id: ticket.number,
    serviceName: ticket.short_description,
    status: status,
    lastDateTime: formatDate(ticket.sys_updated_on),
    originalLastDateTime: ticket.sys_updated_on,
    serviceDescription: ticket.description,
    submittedDate: formatDate(ticket.opened_at || ticket.sys_created_on),
    originalSubmittedDate: ticket.opened_at || ticket.sys_created_on,
    sys_id: ticket.sys_id,
    priority: ticket['priority$DISPLAY'] || ticket.priority || '',
    type: 'request',

    performer: ticket.opened_by?.user_name || '',
    requested_for: ticket.opened_for?.user_name || ticket.caller_id?.user_name || ticket.requested_for?.user_name || '',

    taskDate: '',
    cost: '',
    onBehalf: '',
    requisitionDueDate: '',
    rejection_reason: '',
    cmdb_ci: ticket.cmdb_ci?.name || '',

    comments: formatComments(ticket.journal),
    attachments: formatAttachments(ticket.attachments || []),
  };

  if (status === 'Resolved' || status === 'Closed') {
    formattedItem.resolution = ticket.close_notes;
    formattedItem.closedDate = formatDate(ticket.closed_at || ticket.resolved_at);
  }
  if (status === 'Pending') {
    formattedItem.statusReason = ticket['hold_reason$DISPLAY'];
  }

  return formattedItem;
};